import { ArrowRight, LogIn, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import usePublicEconomics from '@/hooks/usePublicEconomics';

const HeroSection = ({ referralCode = null }) => {
  const { authUser, bootLoading } = useAuth();
  const { heartbeat } = usePublicEconomics();
  const isAuthed = !bootLoading && !!authUser;
  const signupHref = referralCode ? `/signup?ref=${encodeURIComponent(referralCode)}` : '/signup';

  return (
    <section className="relative" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-10 md:pb-16 relative z-10">
        <div className="sc-pill sc-fade-in" style={{ animationDelay: '0.05s' }}>
          <span className="sc-dot" />
          Hybrid Queue Engine v3.0 · Live Architecture
        </div>

        <h1
          className="font-display mt-6 max-w-5xl text-[44px] leading-[1.02] md:text-[72px] md:leading-[1.0] font-bold tracking-tight sc-fade-in"
          style={{ animationDelay: '0.1s' }}
        >
          Skip the stall. <span className="sc-gold-text">Non-stop cycles powered by 1-Year Sub Packs.</span>
        </h1>

        <p className="mt-6 max-w-2xl text-[17px] md:text-[19px] leading-relaxed text-[var(--sc-text-dim)] sc-fade-in" style={{ animationDelay: '0.18s' }}>
          1SimpleCycler 3.0 replaces rigid matrix bottlenecks with rapid 3&times;1 mini-cycles driven by an alternating heartbeat engine: one heartbeat draws
          randomly from the pool, and the next heartbeat clears positions in strict First-In, First-Out (FIFO) order{' '}
          <strong className="text-[#FFE08A]">(currently pacing at a live heartbeat every {heartbeat})</strong>. Backed by 52-week Sub Packs that continuously
          drip fresh fillers into the pool 24/7/365, momentum never runs dry.
        </p>

        <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sc-fade-in" style={{ animationDelay: '0.26s' }}>
          {isAuthed ? (
            <Link to="/dashboard" className="sc-btn-gold" data-testid="hero-cta">
              <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
              Go to Dashboard
            </Link>
          ) : (
            <>
              <Link to={signupHref} className="sc-btn-gold" data-testid="hero-cta-signup">
                <UserPlus className="w-4 h-4" strokeWidth={2.6} />
                Sign Up Now
              </Link>
              <Link to="/login" className="sc-btn-ghost" data-testid="hero-cta-login">
                <LogIn className="w-4 h-4" strokeWidth={2.4} />
                Login
              </Link>
            </>
          )}
        </div>

        <div
          className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs uppercase tracking-[0.18em] text-[var(--sc-text-mute)] sc-fade-in"
          style={{ animationDelay: '0.34s' }}
        >
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB612]" />
            Alternating Random &amp; FIFO Engine
          </span>
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB612]" />
            52-Week Dripping Sub Packs
          </span>
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB612]" />
            Speedy Pack Utility
          </span>
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB612]" />
            LTC · TRX · DOGE
          </span>
        </div>
      </div>

      <div className="sc-orbit hidden md:block" style={{ width: 520, height: 520, top: 60, right: -120 }} />
      <div className="sc-orbit hidden md:block" style={{ width: 320, height: 320, top: 200, right: 40, opacity: 0.6 }} />
    </section>
  );
};

export default HeroSection;
